import type { ComponentType, ReactNode } from 'react';
import styled from '@emotion/styled';
import { useLocation, useNavigate } from '@trinity/chat-components/containers/Chat/RoutingProvider';

export type SettingsNavItem = {
  label: string;
  to: string;
  icon: ComponentType<{ size?: number }>;
};

type Props = {
  navItems: SettingsNavItem[];
  children: ReactNode;
};

export const SettingsLayout = ({ navItems, children }: Props) => {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <Root>
      <Sidebar>
        <SidebarNav>
          {navItems.map((item) => {
            const isActive = location.pathname.startsWith(item.to);
            return (
              <SidebarLink key={item.to} type="button" active={isActive} onClick={() => navigate(item.to)}>
                <SidebarLinkIcon>
                  <item.icon size={25} />
                </SidebarLinkIcon>
                {item.label}
              </SidebarLink>
            );
          })}
        </SidebarNav>
      </Sidebar>
      <Content>{children}</Content>
    </Root>
  );
};

const Root = styled.div({
  display: 'flex',
  maxWidth: 1280,
  margin: '25px auto 0',
  padding: '0 15px',
});

const Sidebar = styled.aside({
  width: 250,
  minWidth: 250,
  flexShrink: 0,
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  padding: '0 25px 0 0',
});

const SidebarNav = styled.nav({
  display: 'flex',
  flexDirection: 'column',
});

const SidebarLink = styled.button<{ active: boolean }>(({ active }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: 10,
  fontFamily: 'var(--font-bold)',
  color: active ? 'var(--black-l)' : 'var(--gray-d)',
  background: 'none',
  border: 'none',
  borderRadius: 6,
  backgroundColor: active ? 'var(--gray-xl)' : 'transparent',
  cursor: 'pointer',
  width: '100%',
  textAlign: 'left',
  marginBottom: 3,
  transition: 'background-color 0.15s ease',

  '&:hover': {
    backgroundColor: active ? 'var(--gray-xl)' : 'var(--gray-xxl)',
  },
}));

const SidebarLinkIcon = styled.span({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  marginRight: 15,
  height: 25,
  width: 25,
});

const Content = styled.main({
  flex: 1,
  minHeight: 'calc(100vh - var(--navbar-height) - 50px)',
  maxWidth: 800,
  border: '1px solid var(--sec-01)',
  borderRadius: 20,
  padding: 25,
  boxSizing: 'border-box',
});
